import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ReadOnlyContext = createContext();

export const useReadOnly = () => {
  const context = useContext(ReadOnlyContext);
  if (!context) {
    throw new Error('useReadOnly must be used within ReadOnlyProvider');
  }
  return context;
};

export const ReadOnlyProvider = ({ children }) => {
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [readonlyToken, setReadonlyToken] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const navigate = useNavigate();

  // Start read-only session
  const startReadOnlySession = useCallback((token, user) => {
    setIsReadOnly(true);
    setTargetUser(user);
    setReadonlyToken(token);
    setSessionStartTime(Date.now());
    
    // Store in sessionStorage for persistence across refreshes
    sessionStorage.setItem('readonly_session', JSON.stringify({
      token,
      user,
      startTime: Date.now()
    }));
    
    toast.success(`Viewing as ${user.name} (Read-only mode)`);
    
    // Force reload to clear any cached data from previous sessions
    window.location.href = '/dashboard';
  }, []);

  // End read-only session
  const endReadOnlySession = useCallback(() => {
    setIsReadOnly(false);
    setTargetUser(null);
    setReadonlyToken(null);
    setSessionStartTime(null);
    setTimeRemaining(null);
    
    // Clear sessionStorage
    sessionStorage.removeItem('readonly_session');
    
    // Navigate back to admin panel
    navigate('/admin');
    toast.info('Exited read-only mode');
  }, [navigate]);

  // Check for existing session on mount
  useEffect(() => {
    const restoreSession = () => {
      const savedSession = sessionStorage.getItem('readonly_session');
      if (savedSession) {
        try {
          const { token, user, startTime } = JSON.parse(savedSession);
          const elapsed = Date.now() - startTime;
          const thirtyMinutes = 30 * 60 * 1000;
          
          if (elapsed < thirtyMinutes) {
            setIsReadOnly(true);
            setTargetUser(user);
            setReadonlyToken(token);
            setSessionStartTime(startTime);
          } else {
            // Session expired
            sessionStorage.removeItem('readonly_session');
          }
        } catch (error) {
          console.error('Failed to restore read-only session:', error);
          sessionStorage.removeItem('readonly_session');
        }
      }
    };
    
    restoreSession();
  }, []);

  // Timer for 30-minute auto-exit
  useEffect(() => {
    if (!isReadOnly || !sessionStartTime) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - sessionStartTime;
      const thirtyMinutes = 30 * 60 * 1000;
      const remaining = thirtyMinutes - elapsed;

      if (remaining <= 0) {
        toast.warning('Read-only session expired (30 minutes)');
        endReadOnlySession();
      } else {
        setTimeRemaining(Math.ceil(remaining / 1000)); // seconds remaining
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isReadOnly, sessionStartTime, endReadOnlySession]);

  const value = {
    isReadOnly,
    targetUser,
    readonlyToken,
    timeRemaining,
    startReadOnlySession,
    endReadOnlySession
  };

  return (
    <ReadOnlyContext.Provider value={value}>
      {children}
    </ReadOnlyContext.Provider>
  );
};
